import { Socket, io } from "socket.io-client"
import { getSocketsUrl } from "@/helpers/getSocketsUrl"

export interface ISocketInitOptions {
  auth?: Record<string, string>
  onConnect?: () => void
  onConnectError?: (error: Error) => void
  onDisconnect?: (reason: string) => void
  onReconnect?: (attemptNumber: number) => void
}

export function createSocketBase(socketName: string) {
  return {
    instance: null as Socket | null,
    dispatch: null as ((action: any) => void) | null,
    _subs: 0,
    _eventSubs: new Map<string, number>(),

    _init(options?: ISocketInitOptions) {
      if (this.instance) return
      this.instance = io(`${getSocketsUrl()}/${socketName}`, {
        auth: options?.auth,
        transports: ["websocket"]
      })
      this.instance.on("connect", () => {
        console.log(`[${socketName}] socket connected`)
        options?.onConnect?.()
      })
      this.instance.on("connect_error", (error) => {
        console.log(`[${socketName}] socket error`)
        options?.onConnectError?.(error)
      })
      this.instance.on("disconnect", (reason) => {
        console.log(`[${socketName}] socket disconnect`)
        options?.onDisconnect?.(reason)
      })
      this.instance.io.on("reconnect", (attemptNumber) => {
        console.log(`[${socketName}] socket reconnect`)
        options?.onReconnect?.(attemptNumber)
      })
    },

    setDispatch(dispatch: (action: any) => void) {
      this.dispatch = dispatch
    },

    addSub() {
      this._subs += 1
    },

    removeSub() {
      this._subs = Math.max(0, this._subs - 1)
      if (this._subs === 0) {
        try {
          this.instance?.disconnect()
        } finally {
          this.instance = null
          this._eventSubs.clear()
        }
      }
    },

    _bind(eventName: string, handler: (payload: any) => void) {
      if (!this.instance) return () => {}

      const currentSubs = this._eventSubs.get(eventName) || 0

      if (currentSubs === 0) {
        this.instance.on(eventName, handler)
      }

      this._eventSubs.set(eventName, currentSubs + 1)

      return () => {
        const subs = this._eventSubs.get(eventName) || 0

        if (subs <= 1) {
          this.instance?.off(eventName, handler)
          this._eventSubs.delete(eventName)
        } else {
          this._eventSubs.set(eventName, subs - 1)
        }
      }
    }
  }
}
